import { useSearchParams } from "react-router-dom";
import { useMetrics } from "~/hooks";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { pluralize } from "~/lib/pluralize";
import { Space } from "~/lib/types";
import {
  FindingType,
  Score,
  ScoreRating,
  ScoreStateFilter,
  useGetAssetInsightsQuery,
} from "~/operations";
import { Metrics } from "~/lib/arrow";
import { Asset } from "../asset/types";
import { useSpaceSettings } from "~/pages/space-settings/hooks/useSpaceSettings";

type UseAssetInsights = {
  assetMrn: string;
  space: Space;
  scope: SpaceOrWorkspaceScope;
  insights: Asset["insights"];
  isCicd: boolean;
};

type AssetInsightsItem = {
  title: string;
  score: Pick<Score, "riskValue" | "riskRating"> | null;
  count: string;
  exceptionsCount: number;
  disabled: boolean;
  to?: string;
};

export function useAssetInsights({
  assetMrn,
  insights,
  space,
  scope,
  isCicd,
}: UseAssetInsights) {
  const { pvcIsEnabled } = useSpaceSettings({ space });
  const [currSearchParams] = useSearchParams();

  const { checksRatingCounts } = useMetrics({
    entityMrn: assetMrn,
    metricMrns: [Metrics.ChecksRatingCounts],
  });

  const { data } = useGetAssetInsightsQuery({
    variables: {
      findingsScopeMrn: assetMrn,
      findingsFirst: 1,
      cveFindingsFilter: {
        types: [FindingType.Cve],
        state: ScoreStateFilter.Open,
        rating: [
          ScoreRating.Low,
          ScoreRating.Medium,
          ScoreRating.High,
          ScoreRating.Critical,
        ],
        includeCicd: isCicd,
      },
      cveExceptionsFilter: {
        types: [FindingType.Cve],
        state: ScoreStateFilter.Exception,
        includeCicd: isCicd,
      },
      checkFindingsFilter: {
        types: [FindingType.Check],
        state: ScoreStateFilter.Open,
        rating: [ScoreRating.High, ScoreRating.Critical],
        includeCicd: isCicd,
      },
      checkExceptionsFilter: {
        types: [FindingType.Check],
        state: ScoreStateFilter.Exception,
        includeCicd: isCicd,
      },
    },
    fetchPolicy: "network-only",
  });

  const buildInsightHref = (key: string) => {
    const paths: Record<string, string | null> = {
      security: "checks",
      vulnerability: "vulnerabilities",
      exceptions: "exceptions",
    };
    const path = paths[key];
    if (!path) return undefined;

    const projectId = currSearchParams.get("projectId");
    const jobId = currSearchParams.get("jobId");

    const searchParams = new URLSearchParams([
      ...scope.params,
      ...(projectId && jobId ? new URLSearchParams({ projectId, jobId }) : []),
    ]);

    if (path === "exceptions") {
      return `exceptions?${searchParams.toString()}`;
    } else if (path === "vulnerabilities") {
      searchParams.set("type", "CVE");
    } else if (path === "checks") {
      searchParams.set("type", "CHECK");
      searchParams.append("riskRating", "CRITICAL");
      searchParams.append("riskRating", "HIGH");
    }

    return `findings?${searchParams.toString()}`;
  };

  // check if the insight is disabled
  // if it's a vulnerability insight, check if the PVC is enabled
  // if it's a security insight, check if there are any policies
  const getDisabledStatus = (value: AssetInsightsItem) => {
    if (value.title === "Vulnerabilities") {
      return !pvcIsEnabled;
    }
    if (value.title === "Security" && !security?.policyCount) {
      return true;
    }
    return false;
  };

  const cveFindings =
    data?.cveFindings?.__typename === "FindingsConnection"
      ? data.cveFindings
      : undefined;

  const cvesCount = cveFindings?.totalCount || 0;

  const { critical, high } = checksRatingCounts;
  const priorityFindingsCount = critical + high;

  // Insights data
  const { security, vulnerability } = insights || {};

  const securityInsight: AssetInsightsItem = {
    title: "Security",
    score: {
      riskRating: security?.score?.riskRating || ScoreRating.None,
      riskValue: security?.score?.riskValue || 0,
    },
    exceptionsCount:
      data?.checkExceptions?.__typename === "FindingsConnection"
        ? data.checkExceptions.totalCount
        : 0,
    count: `${priorityFindingsCount} ${pluralize("Priority finding", priorityFindingsCount)}`,
    disabled: false,
    to: buildInsightHref("security"),
  };

  const vulnsInsight: AssetInsightsItem = {
    title: "Vulnerabilities",
    score: {
      // if theres no rating or risk value, set the score to unknown
      riskRating: vulnerability?.score?.riskRating || ScoreRating.None,
      riskValue: vulnerability?.score?.riskValue || 0,
    },
    exceptionsCount:
      data?.cveExceptions?.__typename === "FindingsConnection"
        ? data.cveExceptions.totalCount
        : 0,
    count: `${cvesCount} ${pluralize("CVE", cvesCount)}`,
    disabled: false,
    to: buildInsightHref("vulnerability"),
  };

  const insightsData = [vulnsInsight, securityInsight]
    .flatMap((i) => i ?? [])
    .map((value) => {
      return {
        ...value,
        disabled: getDisabledStatus(value),
      };
    });

  return {
    insightsData,
  };
}
